import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { GroupTicketView, GroupView, ReplacedMemberView } from "@/services/types";
import { STATUS_LABEL } from "@/config/members";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Avatar } from "@/components/base/Avatar";
import { Tooltip } from "@/components/base/Tooltip";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";
import { ReplaceMemberModal } from "./ReplaceMemberModal";
import { ChangeTicketModal } from "./ChangeTicketModal";
import { MemberRemarksModal, RemarksCell } from "./MemberRemarksModal";
import type { RemarksTarget } from "./remarkUtils";

const MEMBER_BADGE: Record<string, "active" | "inactive" | "cancelled"> = {
  active: "active",
  inactive: "inactive",
  blacklisted: "cancelled",
};

function MemberCell({ ticket }: { ticket: GroupTicketView }) {
  if (!ticket.member) {
    return <span className="text-foreground-400">Empty ticket</span>;
  }
  const m = ticket.member;
  const hasOther = ticket.otherGroups.length > 0;
  return (
    <Link
      to={`/members/${m.id}`}
      className="group/member flex items-center gap-2.5 transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      <Avatar name={m.fullName} size="sm" />
      <div className="min-w-0">
        <span className="block truncate font-medium text-foreground-900 group-hover/member:text-primary-600 group-hover/member:underline">
          {m.fullName}
        </span>
        <span className="tnum block text-xs text-foreground-400">
          {m.memberCode}
          {hasOther ? (
            <Tooltip
              side="right"
              content={
                <span className="block">
                  Also enrolled in {ticket.otherGroups.length} other group{ticket.otherGroups.length === 1 ? "" : "s"}:
                  {ticket.otherGroups.map((g) => (
                    <span key={g.groupId} className="block py-0.5">
                      {g.groupName} · outstanding <CurrencyDisplay amount={g.outstanding} hideSymbol className="text-success-300" />
                    </span>
                  ))}
                </span>
              }
            >
              <span className="ml-1.5 inline-flex cursor-help items-center gap-1 rounded bg-secondary-100 px-1 py-0.5 text-[11px] font-medium text-secondary-800">
                <i className="ri-group-line" />
                {ticket.otherGroups.length}
              </span>
            </Tooltip>
          ) : null}
        </span>
      </div>
    </Link>
  );
}

interface MembersTabProps {
  group: GroupView;
  isViewer?: boolean;
  onMemberReplaced?: () => void;
}

export function MembersTab({ group, isViewer = false, onMemberReplaced }: MembersTabProps) {
  const navigate = useNavigate();
  const [replacingTicket, setReplacingTicket] = useState<GroupTicketView | null>(null);
  const [editingTicket, setEditingTicket] = useState<GroupTicketView | null>(null);
  const [remarksTarget, setRemarksTarget] = useState<RemarksTarget | null>(null);
  const openRemarks = (t: GroupTicketView) =>
    setRemarksTarget({
      groupMemberId: t.groupMemberId!,
      memberName: t.member!.fullName,
      ticketNo: t.seat,
      remarks: t.remarks ?? [],
    });
  const canEditRemarks = !isViewer && (group.status === "draft" || group.status === "active");

  const filled = group.tickets.filter((t) => t.member);
  const canReplace = !isViewer && group.status === "active";
  const canEditTicket = !isViewer && (group.status === "draft" || group.status === "active");

  const columns: DataTableColumn<GroupTicketView>[] = [
    {
      id: "seat",
      header: "Ticket",
      accessor: (t) => (
        <span className="flex items-center gap-1">
          <span className="tnum text-xs font-semibold text-primary-700">#{t.seat}</span>
          {canEditTicket && t.member && t.groupMemberId ? (
            <Tooltip side="right" content="Change ticket number">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTicket(t);
                }}
                title="Change ticket number"
                className="flex h-5 w-5 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                <i className="ri-pencil-line text-xs" />
              </button>
            </Tooltip>
          ) : null}
        </span>
      ),
      sortValue: (t) => t.seat,
      sortable: true,
    },
    {
      id: "member",
      header: "Member",
      accessor: (t) => <MemberCell ticket={t} />,
      sortValue: (t) => t.member?.fullName ?? "",
      sortable: true,
    },
    {
      id: "phone",
      header: "Phone",
      accessor: (t) => <span className="tnum">{t.member ? t.member.primaryPhone : "—"}</span>,
      sortValue: (t) => t.member?.primaryPhone ?? "",
      sortable: true,
    },
    {
      id: "organizer",
      header: "Role",
      accessor: (t) =>
        t.isOrganizer ? (
          <Badge color="accent" dot>Organizer</Badge>
        ) : (
          <span className="text-foreground-400">Member</span>
        ),
      sortValue: (t) => (t.isOrganizer ? 1 : 0),
      sortable: true,
    },
    {
      id: "won",
      header: "Won in",
      accessor: (t) =>
        t.wonCycle != null ? (
          <span className="tnum font-medium text-foreground-900">Cycle {t.wonCycle}</span>
        ) : t.member ? (
          <Badge variant="pending">Waiting</Badge>
        ) : (
          <span className="text-foreground-400">—</span>
        ),
      sortValue: (t) => t.wonCycle ?? 9999,
      sortable: true,
      footerValue: (rows) => (
        <span className="tnum text-xs font-medium text-foreground-600">
          {rows.filter((r) => r.member && r.wonCycle == null).length} still waiting
        </span>
      ),
    },
    {
      id: "outstanding",
      header: "Outstanding",
      align: "right",
      accessor: (t) =>
        t.outstanding > 0 ? (
          <CurrencyDisplay amount={t.outstanding} tone="danger" />
        ) : (
          <CurrencyDisplay amount={0} className="text-foreground-400" />
        ),
      sortValue: (t) => t.outstanding,
      sortable: true,
      footerValue: (rows) => (
        <CurrencyDisplay amount={rows.reduce((s, t) => s + t.outstanding, 0)} hideSymbol />
      ),
    },
    {
      id: "status",
      header: "Status",
      accessor: (t) =>
        t.member ? (
          <Badge variant={MEMBER_BADGE[t.member.status] ?? "inactive"} dot>
            {STATUS_LABEL[t.member.status]}
          </Badge>
        ) : (
          <span className="text-foreground-400">—</span>
        ),
      sortValue: (t) => t.member?.status ?? "",
      sortable: true,
    },
    {
      id: "remarks",
      header: "Remarks",
      accessor: (t) =>
        t.member && t.groupMemberId ? (
          <RemarksCell remarks={t.remarks ?? []} canEdit={canEditRemarks} onOpen={() => openRemarks(t)} />
        ) : (
          <span className="text-foreground-400">—</span>
        ),
      sortValue: (t) => t.remarks?.[0]?.createdAt ?? "",
      sortable: true,
    },
    {
      id: "statement",
      header: "Statement",
      align: "center",
      accessor: (t) =>
        t.member ? (
          <Button
            variant="outline"
            size="sm"
            icon={<i className="ri-file-text-line" />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/reports/statements?memberId=${t.member!.id}&groupId=${group.id}`);
            }}
            className="text-xs hover:border-primary-400 hover:text-primary-700"
          >
            Statement
          </Button>
        ) : (
          <span className="text-foreground-400">—</span>
        ),
    },
  ];

  // Add Replace action column if in active mode
  if (canReplace) {
    columns.push({
      id: "actions",
      header: "Actions",
      align: "right",
      accessor: (t) => {
        if (!t.member) return null;

        if (t.isOrganizer) {
          return (
            <Tooltip side="left" content="Organizer cannot leave or be replaced (Rule 1).">
              <span>
                <Button variant="ghost" size="sm" disabled className="text-foreground-300 opacity-60">
                  <i className="ri-user-shared-line mr-1" /> Replace
                </Button>
              </span>
            </Tooltip>
          );
        }

        if (t.wonCycle != null) {
          return (
            <Tooltip side="left" content={`Member won in Cycle ${t.wonCycle}. Prize winners cannot be replaced (Rule 2 & 3).`}>
              <span>
                <Button variant="ghost" size="sm" disabled className="text-foreground-300 opacity-60">
                  <i className="ri-user-shared-line mr-1" /> Replace
                </Button>
              </span>
            </Tooltip>
          );
        }

        return (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setReplacingTicket(t);
            }}
            className="text-xs hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-user-shared-line mr-1" /> Replace
          </Button>
        );
      },
    });
  }

  const replacedRows: ReplacedMemberView[] = group.replacedMembers ?? [];

  return (
    <div className="space-y-6">
      <DataTable<GroupTicketView>
        columns={columns}
        rows={group.tickets}
        rowKey={(t) => String(t.seat)}
        onRowClick={(t) => {
          if (t.member) navigate(`/members/${t.member.id}`);
        }}
        emptyMessage="No members enrolled yet"
        emptyDescription="Assign members to tickets to fill this group."
        showTotals
        totalLabel={`${filled.length} enrolled`}
        initialPageSize={10}
      />

      {/* Replaced Members / Accounts on Hold Section */}
      {replacedRows.length > 0 ? (
        <div className="rounded-xl border border-background-200 bg-background-50/70 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 pb-3">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-amber-100 text-amber-700">
                <i className="ri-history-line text-sm" />
              </span>
              <h3 className="text-sm font-semibold text-foreground-900">
                Replaced Members (Accounts on Hold)
              </h3>
              <Badge color="warning" size="sm">{replacedRows.length}</Badge>
            </div>
            <span className="text-xs text-foreground-500">
              Hold Mode: Excluded from active demands & auctions
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-foreground-700">
              <thead className="border-b border-background-200 text-[11px] uppercase tracking-wider text-foreground-400">
                <tr>
                  <th className="py-2 px-3">Ticket</th>
                  <th className="py-2 px-3">Exited Member</th>
                  <th className="py-2 px-3">Phone</th>
                  <th className="py-2 px-3">Hold Status</th>
                  <th className="py-2 px-3">Exited On</th>
                  <th className="py-2 px-3">Remarks / Replacement Details</th>
                  <th className="py-2 px-3 text-right">Statement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/80">
                {replacedRows.map((row) => (
                  <tr key={row.groupMemberId} className="hover:bg-background-100/50 transition-colors">
                    <td className="py-2.5 px-3 font-semibold text-primary-700">#{row.ticketNo}</td>
                    <td className="py-2.5 px-3">
                      <Link
                        to={`/members/${row.memberId}`}
                        className="group/member flex items-center gap-2 transition-colors"
                      >
                        <Avatar name={row.fullName} size="xs" />
                        <div>
                          <div className="font-medium text-foreground-900 group-hover/member:text-primary-600 group-hover/member:underline">
                            {row.fullName}
                          </div>
                          <div className="text-[11px] text-foreground-400">{row.memberCode}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="py-2.5 px-3 tnum">{row.phone}</td>
                    <td className="py-2.5 px-3">
                      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        <i className="ri-pause-circle-line" /> REPLACED (HOLD)
                      </span>
                    </td>
                    <td className="py-2.5 px-3 tnum">{row.exitedOn ? formatDate(row.exitedOn) : "—"}</td>
                    <td className="py-2.5 px-3 text-foreground-600 max-w-xs truncate" title={row.remarks ?? ""}>
                      {row.remarks || "Replaced by incoming member"}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<i className="ri-file-text-line" />}
                        onClick={() => navigate(`/reports/statements?memberId=${row.memberId}&groupId=${group.id}`)}
                        className="text-xs hover:border-primary-400 hover:text-primary-700"
                      >
                        Statement
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Replace Member Modal */}
      <ReplaceMemberModal
        open={Boolean(replacingTicket)}
        group={group}
        ticket={replacingTicket}
        onClose={() => setReplacingTicket(null)}
        onSuccess={() => {
          setReplacingTicket(null);
          onMemberReplaced?.();
        }}
      />

      <MemberRemarksModal
        open={Boolean(remarksTarget)}
        groupId={group.id}
        target={remarksTarget}
        readOnly={!canEditRemarks}
        onClose={() => setRemarksTarget(null)}
        onChanged={() => onMemberReplaced?.()}
      />

      <ChangeTicketModal
        open={Boolean(editingTicket)}
        group={group}
        ticket={editingTicket}
        onClose={() => setEditingTicket(null)}
        onSuccess={() => {
          setEditingTicket(null);
          onMemberReplaced?.();
        }}
      />
    </div>
  );
}